import { getFirmwareApiGateway } from 'common';

import { HttpClient } from '../../utils/httpClient';
import { useAsyncState } from '../../utils/useAsyncState';

// Types for backlight API
export type BacklightPattern = 'Off' | 'Solid' | 'Slow Pulse';

export type BacklightColor = 'White' | 'Red' | 'Yellow' | 'Green' | 'Blue' | 'Custom';

export interface BacklightColorRequest {
  color: BacklightColor;
  custom_rgb?: [number, number, number];
}

export interface BacklightStatusData {
  running: boolean;
  current_effect: 'Off' | 'Solid' | 'Breathing' | 'Patriotic' | 'Rainbow' | 'RWB Steady' | 'Mock';
  last_color: string | null;
  last_pattern: string | null;
  last_updated: string | null;
}

export interface BacklightStatus {
  status: BacklightStatusData;
}

export interface BacklightColorResponse {
  color: BacklightColor;
  custom_rgb?: [number, number, number];
}

export interface BacklightPatternResponse {
  pattern: BacklightPattern;
}

export interface BacklightBrightnessResponse {
  brightness: number;
}

export function useBacklight() {
  const httpClient = new HttpClient(getFirmwareApiGateway());

  // Async state for backlight status
  const backlightStatus = useAsyncState(() => httpClient.get<BacklightStatus>('/backlight/status'));

  // Async state for individual properties
  const backlightColor = useAsyncState(() =>
    httpClient.get<BacklightColorResponse>('/backlight/color'),
  );
  const backlightPattern = useAsyncState(() =>
    httpClient.get<BacklightPatternResponse>('/backlight/pattern'),
  );
  const backlightBrightness = useAsyncState(() =>
    httpClient.get<BacklightBrightnessResponse>('/backlight/brightness'),
  );

  // Power control methods
  const turnOnBacklight = async () => {
    // First set the power to on
    await httpClient.post('/backlight/power', { power: true });

    // Refresh status after setting
    await backlightStatus.execute();
  };

  const turnOffBacklight = async () => {
    await httpClient.post('/backlight/power', { power: false });
    // Refresh status after setting
    await backlightStatus.execute();
  };

  const setBacklightPower = async (power: boolean) => {
    await httpClient.post('/backlight/power', { power });
    // Refresh status after setting
    await backlightStatus.execute();
  };

  // Color control methods
  const getBacklightColor = async () => {
    await backlightColor.execute();
    return backlightColor.data;
  };

  const setBacklightColor = async (color: BacklightColor, customRgb?: [number, number, number]) => {
    const payload: BacklightColorRequest = { color };
    if (color === 'Custom' && customRgb) {
      payload.custom_rgb = customRgb;
    }
    await httpClient.post('/backlight/color', payload);
    // Refresh color and status after setting
    await backlightColor.execute();
    await backlightStatus.execute();
  };

  const setBacklightCustomColor = async (rgb: [number, number, number]) => {
    await setBacklightColor('Custom', rgb);
  };

  // Pattern control methods
  const getBacklightPattern = async () => {
    await backlightPattern.execute();
    return backlightPattern.data;
  };

  const setBacklightPattern = async (pattern: BacklightPattern) => {
    await httpClient.post('/backlight/pattern', { pattern });
    // Refresh pattern and status after setting
    await backlightPattern.execute();
    await backlightStatus.execute();
  };

  // Brightness control methods
  const getBacklightBrightness = async () => {
    await backlightBrightness.execute();
    return backlightBrightness.data;
  };

  const setBacklightBrightness = async (brightness: number) => {
    // Ensure brightness is within valid range (0-100) and convert to integer
    const clampedBrightness = Math.max(0, Math.min(100, Math.round(brightness)));
    await httpClient.post('/backlight/brightness', { brightness: clampedBrightness });
    // Refresh brightness and status after setting
    await backlightBrightness.execute();
    await backlightStatus.execute();
  };

  // Refresh all data
  const refreshAll = async () => {
    await Promise.all([
      backlightStatus.execute(),
      backlightColor.execute(),
      backlightPattern.execute(),
      backlightBrightness.execute(),
    ]);
  };

  return {
    // Power control
    turnOnBacklight,
    turnOffBacklight,
    setBacklightPower,

    // Color control
    getBacklightColor,
    setBacklightColor,
    setBacklightCustomColor,

    // Pattern control
    getBacklightPattern,
    setBacklightPattern,

    // Brightness control
    getBacklightBrightness,
    setBacklightBrightness,

    // Utility
    refreshAll,

    // Async state objects for direct access
    backlightStatus,
    backlightColor,
    backlightPattern,
    backlightBrightness,
  };
}
